/**
 * Avenças: services billed every month (Gshock Fee Mensal) instead of one
 * fixed Valor. Each client invoice carries its month as `period: "YYYY-MM"`.
 */
export interface AvencaFields {
  campaign_mode?: string | null;
  billing_type?: string | null;
  monthly_amount?: number | string | null;
  billing_start?: string | null;
  billing_end?: string | null;
  mohit_invoices?: unknown;
}

export function isAvenca(c: AvencaFields): boolean {
  return c.campaign_mode === 'services' && c.billing_type === 'monthly';
}

/** "2026-10" from a Date, "2026-10-01", or "2026-10". */
export function toMonthKey(value: Date | string | null | undefined): string | null {
  if (!value) return null;
  if (value instanceof Date) {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}`;
  }
  const m = /^(\d{4})-(\d{2})/.exec(value);
  return m ? `${m[1]}-${m[2]}` : null;
}

function nextMonth(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
}

/** Months to bill from the start up to this month (or the end month, if earlier). */
export function billingMonthsDue(start: string | null | undefined, end: string | null | undefined, today = new Date()): string[] {
  const first = toMonthKey(start);
  if (!first) return [];
  const current = toMonthKey(today) as string;
  const last = toMonthKey(end);
  const stop = last && last < current ? last : current;
  const months: string[] = [];
  for (let k = first; k <= stop && months.length < 240; k = nextMonth(k)) months.push(k);
  return months;
}

function invoicePeriods(c: AvencaFields): Set<string> {
  let list: unknown = c.mohit_invoices;
  if (typeof list === 'string') {
    try { list = JSON.parse(list); } catch { list = []; }
  }
  const periods = new Set<string>();
  if (Array.isArray(list)) {
    for (const inv of list) {
      const p = toMonthKey((inv as { period?: string })?.period);
      if (p) periods.add(p);
    }
  }
  return periods;
}

/** Months already due with no client invoice yet — the monthly reminder. */
export function getAvencaMissingMonths(c: AvencaFields, today = new Date()): string[] {
  if (!isAvenca(c)) return [];
  const have = invoicePeriods(c);
  return billingMonthsDue(c.billing_start, c.billing_end, today).filter((m) => !have.has(m));
}

/** The month a "Gerar fatura" click should create: the first missing one. */
export function getNextAvencaMonth(c: AvencaFields, today = new Date()): string | null {
  return getAvencaMissingMonths(c, today)[0] ?? null;
}

/** Ended = it had an end month and that month is over. */
export function isAvencaEnded(c: AvencaFields, today = new Date()): boolean {
  const end = toMonthKey(c.billing_end);
  return !!end && end < (toMonthKey(today) as string);
}

const MONTHS_PT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** "out 2026" — fixed names, so it reads the same in every browser */
export function formatMonthPt(key: string | null | undefined): string {
  const k = toMonthKey(key);
  if (!k) return '';
  const [y, m] = k.split('-').map(Number);
  return `${MONTHS_PT[m - 1]} ${y}`;
}
